import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-preact'
import { Dialog } from './Dialog'
import { Button } from './Button'
// Removed Loader2 import

interface ConfirmationProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  message?: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info'
  isLoading?: boolean
}

export function Confirmation({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'warning',
  isLoading = false,
}: ConfirmationProps) {
  const { t } = useTranslation()

  const defaultTitle = title || t('common.confirmation.title', 'Potwierdzenie')
  const defaultMessage =
    message ||
    t('common.confirmation.message', 'Czy na pewno chcesz wykonać tę akcję?')
  const defaultConfirmText =
    confirmText || t('common.confirmation.confirm', 'Potwierdź')
  const defaultCancelText =
    cancelText || t('common.confirmation.cancel', 'Anuluj')

  const variantStyles = {
    danger: {
      icon: 'text-red-500',
    },
    warning: {
      icon: 'text-yellow-500',
    },
    info: {
      icon: 'text-blue-500',
    },
  }

  const currentVariant = variantStyles[variant]

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm()
    }
  }

  const handleCancel = () => {
    if (!isLoading) {
      onClose()
    }
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      disableMobileBehavior={true}
      className='max-w-lg sm:max-w-md'
    >
      {isLoading && (
        <div className='absolute inset-0 z-50 bg-white/80 flex items-center justify-center pointer-events-none'>
          <div className='w-16 h-16 border-4 border-cencert-border-gray border-t-cencert-blue rounded-full animate-spin'></div>
        </div>
      )}

      <div className='p-6 sm:p-8'>
        {/* Icon and Title */}
        <div className='flex items-center gap-4 mb-6'>
          <div className={`flex-shrink-0 ${currentVariant.icon}`}>
            <AlertTriangle size={28} strokeWidth={2} />
          </div>
          <h3 className='text-heading-5 sm:text-heading-4 text-cencert-header-blue leading-tight'>
            {defaultTitle}
          </h3>
        </div>

        {/* Message */}
        <div className='mb-8'>
          <p className='text-body text-cencert-text-gray leading-relaxed'>
            {defaultMessage}
          </p>
        </div>

        {/* Action Buttons */}
        <div className='flex flex-col xs:flex-row gap-3 xs:gap-4'>
          {/* Cancel Button */}
          <Button
            type='secondary'
            color='primary'
            size='md'
            onClick={handleCancel}
            disabled={isLoading}
            className='w-full xs:flex-1 xs:min-w-[140px]'
          >
            {defaultCancelText}
          </Button>

          {/* Confirm Button */}
          <Button
            type='primary'
            color={variant === 'danger' ? 'danger' : 'primary'}
            size='md'
            onClick={handleConfirm}
            disabled={isLoading}
            className='w-full xs:flex-1 xs:min-w-[140px]'
          >
            {defaultConfirmText}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
